import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { Modal, Button, Typography, Space, message } from "antd";
import { CopyOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { cleanBybitFromMessage } from "../utils/messageUtils";

const { Title, Text } = Typography;

/**
 * Internal modal component. Always renders open; closes via onClose().
 */
function OrderSuccessModal({ orderId, symbol, side, positionIdx, isSuccess = true, errorMessage, onClose }) {
  const [copied, setCopied] = useState(false);

  // Determine position type based on side and positionIdx
  const getPositionType = () => {
    if (side === 'Buy' && positionIdx === 1) return 'Open Long';
    if (side === 'Sell' && positionIdx === 2) return 'Open Short';
    if (side === 'Sell' && positionIdx === 1) return 'Close Long';
    if (side === 'Buy' && positionIdx === 2) return 'Close Short';
    return `${side} (Pos: ${positionIdx})`;
  };

  const handleCopy = async () => {
    try {
      let textToCopy;
      if (isSuccess && orderId) {
        // For success, copy last 8 characters of order ID
        textToCopy = String(orderId).slice(-8);
      } else {
        // For failure, copy cleaned error message
        textToCopy = cleanBybitFromMessage(String(errorMessage || ""));
      }
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      message.success(isSuccess ? 'Order ID copied to clipboard!' : 'Error message copied to clipboard!');
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      console.error("Clipboard error:", e);
      message.error('Failed to copy to clipboard');
    }
  };

  return (
    <Modal
      open={true}
      onCancel={onClose}
      footer={null}
      centered
      width={450}
      style={{ textAlign: 'center' }}
    >
      <div style={{ padding: '20px 0' }}>
        <div style={{ marginBottom: 16 }}>
          {isSuccess ? (
            <CheckOutlined 
              style={{ 
                fontSize: 48, 
                color: '#52c41a', 
                marginBottom: 16 
              }} 
            />
          ) : (
            <CloseOutlined 
              style={{ 
                fontSize: 48, 
                color: '#ff4d4f', 
                marginBottom: 16 
              }} 
            />
          )}
        </div>
        
        <Title level={3} style={{ marginBottom: 8, color: isSuccess ? '#52c41a' : '#ff4d4f' }}>
          {isSuccess ? 'Order Success!' : 'Order Failed!'}
        </Title>
        
        <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
          {isSuccess ? 'Your order was created successfully.' : 'Your order could not be processed.'}
        </Text>
        
        {/* Order Details */}
        <div style={{ 
          background: '#f5f5f5', 
          padding: '16px', 
          borderRadius: 6, 
          marginBottom: 20,
          border: '1px solid #d9d9d9',
          textAlign: 'left'
        }}>
          {symbol && (
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ color: '#666', fontSize: 13 }}>Symbol: </Text>
              <Text strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{symbol}</Text>
            </div>
          )}
          {side && positionIdx && (
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ color: '#666', fontSize: 13 }}>Type: </Text>
              <Text strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{getPositionType()}</Text>
            </div>
          )}
          {/* {positionIdx && (
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ color: '#666', fontSize: 13 }}>Position Index: </Text>
              <Text strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{positionIdx}</Text>
            </div>
          )} */}
          {isSuccess && orderId && (
            <div>
              <Text strong style={{ color: '#666', fontSize: 13 }}>Order ID: </Text>
              <Text strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{String(orderId).slice(-8)}</Text>
            </div>
          )}
          {!isSuccess && errorMessage && (
            <div>
              <Text strong style={{ color: '#666', fontSize: 13 }}>Error: </Text>
              <Text strong style={{ fontFamily: 'monospace', fontSize: 14, color: '#ff4d4f' }}>{cleanBybitFromMessage(String(errorMessage))}</Text>
            </div>
          )}
        </div>
        
        <Space size="middle">
          <Button 
            icon={copied ? <CheckOutlined /> : <CopyOutlined />} 
            onClick={handleCopy}
            type={copied ? "default" : "default"}
          >
            {copied ? "Copied!" : (isSuccess ? "Copy ID" : "Copy Error")}
          </Button>
          <Button type="primary" onClick={onClose}>
            OK
          </Button>
        </Space>
      </div>
    </Modal>
  );
}

/**
 * Call this anywhere in client-side code to show the modal.
 * Examples: 
 * openOrderSuccess({ orderId: "ORD-12345", symbol: "BTCUSDT", side: "Buy", positionIdx: 1 })
 * openOrderSuccess({ symbol: "ETHUSDT", side: "Sell", positionIdx: 2, isSuccess: false, errorMessage: "Insufficient funds" })
 */
export function openOrderSuccess({ orderId, symbol, side, positionIdx, isSuccess = true, errorMessage }) {
  if (typeof window === "undefined") return; // guard for SSR
  
  // Don't show modal if no essential data is provided
  if (!symbol && !orderId && !errorMessage) return;

  // Clean the error message before displaying
  const cleanedErrorMessage = errorMessage ? cleanBybitFromMessage(String(errorMessage)) : errorMessage;

  const mountEl = document.createElement("div");
  document.body.appendChild(mountEl);

  const root = createRoot(mountEl);

  const cleanup = () => {
    try {
      root.unmount();
    } catch {}
    if (mountEl && mountEl.parentNode) {
      mountEl.parentNode.removeChild(mountEl);
    }
  };

  root.render(
    <OrderSuccessModal 
      orderId={orderId}
      symbol={symbol}
      side={side}
      positionIdx={positionIdx}
      isSuccess={isSuccess}
      errorMessage={cleanedErrorMessage}
      onClose={cleanup} 
    />
  );
}

export default OrderSuccessModal;
